/**
 * chain-eos/account-create.ts
 *
 * Create a brand-new on-chain account, paid for by an already-logged-in
 * (payer) account: newaccount + buyrambytes + optional delegatebw, all in
 * one transaction. Mirrors the "creator pays" flow from blocksfinity.com's
 * /wallet/createaccount, adapted to our stack.
 *
 * The new account's owner/active permission is a single key (threshold 1),
 * generated client-side or supplied by the caller. The caller is responsible
 * for getting that private key into the wallet's own storage afterwards
 * (see accountManager.addAccount in @properwallet/wallet-core) — this module
 * only talks to the chain.
 */

import { Asset, Name, PrivateKey, PublicKey } from '@wharfkit/antelope'
import type { Session } from './session.js'
import { SYSTEM_CONTRACT } from './config.js'
import { accountExists } from './resources.js'

export interface NewKeyPair {
  privateKey: string
  publicKey: string
}

/** Generates a fresh K1 keypair client-side. Never sent anywhere but into wallet storage. */
export function generateKeyPair(): NewKeyPair {
  const pk = PrivateKey.generate('K1')
  return { privateKey: pk.toWif(), publicKey: pk.toPublic().toString() }
}

/**
 * Antelope charges RAM for the account row itself plus both permissions
 * (owner + active). ~3000 bytes is the conventional safe floor most
 * explorers (blocksfinity included) default new accounts to — enough for
 * the account plus a little headroom, not enough to do much on-chain
 * without buying more RAM later.
 */
export const MIN_NEW_ACCOUNT_RAM_BYTES = 3000

/** Standard 1-12 char Antelope account name charset (a-z, 1-5, dot). */
const ACCOUNT_NAME_RE = /^[a-z1-5.]{1,12}$/

export function isValidAccountName(name: string): boolean {
  const n = name.trim()
  return ACCOUNT_NAME_RE.test(n) && !n.endsWith('.')
}

export interface CreateAccountParams {
  /** Pays RAM/CPU/NET — the currently logged-in (active) account. */
  payer: string
  /** Name of the account being created. */
  newAccount: string
  /** Public key for the owner permission. */
  ownerKey: string
  /** Public key for the active permission. Defaults to ownerKey. */
  activeKey?: string
  /** RAM bytes bought for the new account (payer pays). */
  ramBytes: number
  /** e.g. "0.1000 EOS" — CPU stake for the new account. Omit/zero to skip delegatebw. */
  cpuStakeEos?: string
  /** e.g. "0.1000 EOS" — NET stake for the new account. Omit/zero to skip delegatebw. */
  netStakeEos?: string
  /**
   * If true, the staked CPU/NET becomes the new account's own (irrevocable
   * transfer, like buying it resources outright) instead of a revocable
   * delegation the payer can unstake later. Defaults to false — matches
   * what buyRam/PowerUp already do elsewhere in this app (payer keeps the
   * ability to reclaim resources).
   */
  transferStake?: boolean
  /** Which permission on `payer` signs this — never hardcode 'active'. */
  payerPermission: string
}

/** Throws a user-facing error if the requested name is unusable, without touching the chain. */
export function assertCreatableAccountName(name: string): void {
  const n = name.trim()
  if (!isValidAccountName(n)) {
    throw new Error('Account name must be 1-12 characters, a-z and 1-5 only')
  }
}

/** True if `name` is free to register (well-formed and not already on chain). */
export async function isAccountNameAvailable(name: string): Promise<boolean> {
  if (!isValidAccountName(name)) return false
  return !(await accountExists(name))
}

function hasStake(assetStr: string | undefined): boolean {
  if (!assetStr) return false
  const amount = parseFloat(assetStr.trim().split(/\s+/)[0] ?? '0')
  return Number.isFinite(amount) && amount > 0
}

export async function createAccount(
  session: Session,
  params: CreateAccountParams,
): Promise<string> {
  const payer = params.payer.trim()
  const newAccount = params.newAccount.trim().toLowerCase()
  assertCreatableAccountName(newAccount)

  const ownerPub = PublicKey.from(params.ownerKey)
  const activePub = params.activeKey ? PublicKey.from(params.activeKey) : ownerPub
  const auth = [{ actor: payer, permission: params.payerPermission }]

  const singleKeyAuth = (key: PublicKey) => ({
    threshold: 1,
    keys: [{ key, weight: 1 }],
    accounts: [],
    waits: [],
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actions: any[] = [
    {
      account: SYSTEM_CONTRACT,
      name: 'newaccount',
      authorization: auth,
      data: {
        creator: Name.from(payer),
        name: Name.from(newAccount),
        owner: singleKeyAuth(ownerPub),
        active: singleKeyAuth(activePub),
      },
    },
    {
      account: SYSTEM_CONTRACT,
      name: 'buyrambytes',
      authorization: auth,
      data: {
        payer: Name.from(payer),
        receiver: Name.from(newAccount),
        bytes: Math.max(MIN_NEW_ACCOUNT_RAM_BYTES, Math.round(params.ramBytes)),
      },
    },
  ]

  if (hasStake(params.cpuStakeEos) || hasStake(params.netStakeEos)) {
    actions.push({
      account: SYSTEM_CONTRACT,
      name: 'delegatebw',
      authorization: auth,
      data: {
        from: Name.from(payer),
        receiver: Name.from(newAccount),
        stake_net_quantity: Asset.from(params.netStakeEos ?? '0.0000 EOS'),
        stake_cpu_quantity: Asset.from(params.cpuStakeEos ?? '0.0000 EOS'),
        transfer: !!params.transferStake,
      },
    })
  }

  const result = await session.transact({ actions })
  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}
